import { IconSelector, IconX } from "@tabler/icons-react";
import {
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ClipboardEvent,
  type ReactNode,
} from "react";
import {
  Button,
  ComboBoxStateContext,
  ComboBoxValue,
  Group,
  Input,
  type ComboBoxState,
  type Key,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import type { Size } from "../helpers/helpers.js";
import { useComboboxAutoHighlight } from "./comboboxAutoHighlight.js";
import {
  isFieldMutable,
  useComboboxChipHighlight,
} from "./comboboxChipHighlight.js";
import { useComboboxFieldBehavior } from "./comboboxFieldBehavior.js";
import {
  matchPastedList,
  readPasteOptions,
  readPastedList,
} from "./comboboxPaste.js";
import { noteKeyboardInput } from "./comboboxRemoval.js";
import { warnOnce } from "./devWarning.js";
import { comboboxStyle } from "./style.js";

export interface ComboboxChipsProps<T extends object> {
  /** Renders the content of the chip for a selected item. */
  children: (item: T) => ReactNode;
  /**
   * Plain-text equivalent of the chip content, announced to screen readers.
   * Required when `children` renders non-string content.
   */
  textValue?: (item: T) => string;
  /** Accessible name for the group of selected-item chips. */
  chipsLabel?: string;
  placeholder?: string;
  size?: typeof Size.SMALL | typeof Size.MEDIUM;
  autoFocus?: boolean;
  /**
   * Paste into the filter input, ahead of the built-in list handling below.
   * Call `preventDefault()` to take the paste over entirely; to extend it
   * rather than replace it, use `onPasteUnmatched`.
   */
  onPaste?: (event: ClipboardEvent<HTMLInputElement>) => void;
  /**
   * The values in a pasted list that named no option, deduplicated. Return the
   * keys to select for them — that is how {@link CreatableComboboxField} turns a
   * pasted list into new values. Without this they are dropped, a fixed list of
   * options having nothing to create.
   */
  onPasteUnmatched?: (values: readonly string[]) => readonly Key[];
  className?: string;
}

/**
 * The field of a multi-select {@link Combobox}: selected items rendered as
 * removable chips, followed by the filter input and a chevron button to toggle
 * the full list. Requires the combobox to use a dynamic collection (`items` or
 * `defaultItems`) so the selected item objects are available to render chips.
 *
 * The whole field is one control with a single tab stop, following the
 * WAI-ARIA combobox pattern: DOM focus always stays on the input. Chips are
 * highlighted — not focused — with ArrowLeft from the start of the input,
 * navigated with the arrow keys (exposed via aria-activedescendant), and
 * removed with Backspace or their remove button. Known limit: the remove
 * button is `aria-hidden` (an `option` may not own interactive descendants),
 * so a touch screen-reader user, who has no Backspace, can't remove a chip
 * without a keyboard.
 *
 * Pasting a comma, semicolon, newline or tab separated list selects every
 * option it names — by label or by key, matched the way typing is (ignoring
 * case, accents and whitespace) — so a list from elsewhere becomes chips in one
 * go. Values that match no option are dropped: there is nothing to create here.
 * A single pasted value is left to the filter input.
 */
export const ComboboxChips = <T extends object>(
  props: ComboboxChipsProps<T>,
) => {
  const {
    children,
    textValue,
    chipsLabel,
    placeholder,
    size,
    autoFocus,
    onPaste,
    onPasteUnmatched,
    className,
  } = props;
  // The context is typed over `any` items; narrow to `object` for safe use.
  const state = useContext(ComboBoxStateContext) as ComboBoxState<
    object,
    "single" | "multiple"
  > | null;
  const style = comboboxStyle({ size, hasChips: true });
  const inputRef = useRef<HTMLInputElement>(null);

  const value = state?.value;
  const selectedKeys = useMemo<readonly Key[]>(
    () => (Array.isArray(value) ? value : []),
    [value],
  );

  // Both miswirings below leave a field that renders and focuses like any
  // other while quietly holding no chips, so say so rather than let it pass
  // for a styling problem.
  const hasState = state !== null;
  const hasArrayValue = Array.isArray(value);
  // A selected node without an item object: a static child, or a section key
  // (whose chip can't be rendered — or removed — either way). Not something a
  // key still loading can trigger: those have no node in the collection at all,
  // so they never reach `selectedItems`.
  const hasUnrenderableChip = (state?.selectedItems ?? []).some(
    (node) => node.value == null,
  );
  useEffect(() => {
    if (hasState && !hasArrayValue) {
      warnOnce(
        'ComboboxChips is the field of a multi-select combobox, but this one selects a single value. Pass selectionMode="multiple" to <Combobox>, or use <ComboboxInput> instead.',
      );
    }
    if (hasUnrenderableChip) {
      warnOnce(
        "ComboboxChips renders a chip from the item object behind an option, which a selected key here doesn't have. Build the options from a dynamic collection (`items`/`defaultItems`) rather than static children, and keep section keys out of the value.",
      );
    }
  }, [hasState, hasArrayValue, hasUnrenderableChip]);

  const {
    highlightedKey,
    highlightChip,
    dropHighlight,
    removeKey,
    chipsListboxId,
    chipId,
    handleInputKeyDown,
    handleInputKeyDownCapture,
  } = useComboboxChipHighlight({ state, selectedKeys, inputRef });

  const { handleFieldPointerDown, handleFieldPointerUp } =
    useComboboxFieldBehavior(inputRef, {
      interactiveSelector: '[role="option"]',
    });
  const { activeDescendant, handleKeyDownCapture } = useComboboxAutoHighlight();

  // Selecting an option pushes the input down a row once the chips wrap, and
  // out of sight once they scroll. Follow it, so what the user types next is
  // where they can see it. Keyed on the text as well as the chips: react-aria
  // clears the input after a selection, which reflows the rows once more.
  const inputValue = state?.inputValue;
  useEffect(() => {
    if (document.activeElement !== inputRef.current) {
      return;
    }
    inputRef.current?.scrollIntoView({ block: "nearest" });
  }, [selectedKeys, inputValue]);

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    // Ahead of the consumer's handler: a field that can't be mutated shouldn't
    // be running paste handlers at all.
    if (!state || !isFieldMutable(inputRef.current)) {
      return;
    }
    onPaste?.(event);
    if (event.defaultPrevented) {
      return;
    }

    const values = readPastedList(event.clipboardData.getData("text"));
    if (values === null) {
      return;
    }

    event.preventDefault();
    const { added, unmatched } = matchPastedList(values, {
      // The selection manager's collection, which — unlike `state.collection` —
      // is never filtered: the pasted list replaces whatever was typed, so it
      // matches against every option.
      options: readPasteOptions(
        state.selectionManager.collection,
        (item) => textValue?.(item as T) ?? children(item as T),
      ),
      isDisabled: (key) => state.selectionManager.isDisabled(key),
      isSelected: (key) => selectedKeys.some((selected) => selected === key),
    });

    const selected = new Set(selectedKeys.map(String));
    const addedKeys = [...added];
    for (const key of unmatched.length > 0
      ? (onPasteUnmatched?.(unmatched) ?? [])
      : []) {
      if (!selected.has(String(key)) && !addedKeys.includes(key)) {
        addedKeys.push(key);
      }
    }

    if (addedKeys.length > 0) {
      // Only once something lands: react-aria doesn't reset a controlled input
      // itself, and wiping a search that produced nothing would just lose work.
      state.setInputValue("");
      state.setValue([...selectedKeys, ...addedKeys]);
    }
  };

  return (
    <Group
      className={style.field({ className })}
      onPointerDown={handleFieldPointerDown}
      onPointerUp={handleFieldPointerUp}
    >
      <div className={style.chipsWrap()}>
        <ComboBoxValue<T> className="contents">
          {({ state: valueState }) => {
            // state.selectedItems pairs each key with its collection node, so
            // keys missing from the collection (async loading, stale values)
            // can't shift chips onto the wrong labels the way index-zipping
            // state.value with the render prop's selectedItems would.
            // `!= null`: a node built from static children has an undefined
            // value, as does a section node — which a section key in the value
            // puts here, where rendering it would throw.
            const chips = valueState.selectedItems
              .map((node) => ({ key: node.key, item: node.value }))
              .filter(
                (chip): chip is { key: Key; item: T } => chip.item != null,
              );

            if (chips.length === 0) {
              // No empty listbox in the accessibility tree.
              return null;
            }

            return (
              <div
                id={chipsListboxId}
                role="listbox"
                aria-label={chipsLabel ?? "Selected options"}
                aria-orientation="horizontal"
                className="contents"
              >
                {chips.map(({ key, item }) => {
                  const content = children(item);
                  const isHighlighted = key === highlightedKey;

                  return (
                    <div
                      key={chipId(key)}
                      id={chipId(key)}
                      role="option"
                      aria-selected
                      data-highlighted={isHighlighted || undefined}
                      className={style.chip()}
                      // Clicking a chip highlights it while keeping DOM focus
                      // on the input. Disabled fields don't respond at all;
                      // read-only ones still allow highlighting (navigation
                      // without mutation).
                      onPointerDown={(event) => {
                        if (
                          inputRef.current?.disabled ||
                          (event.target as HTMLElement).closest("button")
                        ) {
                          return;
                        }
                        event.preventDefault();
                        inputRef.current?.focus();
                        highlightChip(key);
                      }}
                    >
                      <span className="truncate">
                        {textValue ? (
                          <span aria-hidden>{content}</span>
                        ) : (
                          content
                        )}
                      </span>
                      {textValue ? (
                        <span className="sr-only">{textValue(item)}</span>
                      ) : null}
                      {/* Mouse-only affordance: keyboard users remove the
                          highlighted chip with Backspace. */}
                      <button
                        type="button"
                        tabIndex={-1}
                        aria-hidden
                        className={style.chipRemove()}
                        // As on the chip itself: without this the press moves
                        // DOM focus off the input, and react-aria commits the
                        // combobox on the way — closing the popup and wiping
                        // the search — before the click even removes anything.
                        onPointerDown={(event) => {
                          event.preventDefault();
                        }}
                        onClick={() => {
                          removeKey(key);
                        }}
                      >
                        <IconX aria-hidden className={style.chipRemoveIcon()} />
                      </button>
                    </div>
                  );
                })}
              </div>
            );
          }}
        </ComboBoxValue>
        <div className={style.inputWrap()}>
          <span aria-hidden className={style.inputSizer()}>
            {/* The trailing space keeps room for the caret and gives the
                sizer height when there's neither text nor placeholder. */}
            {state?.inputValue || placeholder || ""}{" "}
          </span>
          <Input
            ref={inputRef}
            // While a chip is highlighted the interaction "left" the text
            // field, so the caret is hidden even though DOM focus stays here.
            className={style.input({
              className: twMerge(
                "absolute inset-0",
                highlightedKey !== null && "caret-transparent",
              ),
            })}
            placeholder={placeholder}
            autoFocus={autoFocus}
            // A highlighted chip owns it; otherwise the popup's, which the
            // auto-highlight withholds while the search is being typed.
            aria-activedescendant={
              highlightedKey !== null
                ? chipId(highlightedKey)
                : activeDescendant
            }
            // Makes the chips logical descendants of the input so the
            // aria-activedescendant reference into them is valid.
            aria-owns={highlightedKey !== null ? chipsListboxId : undefined}
            // The remove buttons are mouse-only, so the keyboard route to
            // removal has to be announced rather than discovered.
            aria-keyshortcuts={
              selectedKeys.length > 0 ? "Backspace" : undefined
            }
            onKeyDown={handleInputKeyDown}
            onKeyDownCapture={(event) => {
              noteKeyboardInput();
              handleInputKeyDownCapture(event);
              handleKeyDownCapture(event);
            }}
            onPaste={handlePaste}
            onBlur={dropHighlight}
          />
        </div>
      </div>
      <Button className={style.trigger()}>
        <IconSelector className={style.chevron()} />
      </Button>
    </Group>
  );
};
